import type { ItemReading } from './jev';
import type { Rubric, RubricItem } from './rubric';
import type { Sentence } from './sentences';

/**
 * The arithmetic Jev must not do. Pass/fail, evidence thresholds and totals, in code, from
 * the probabilities the model returned.
 */

/** A sentence is evidence for an item when the model puts at least this much on it. */
export const EVIDENCE_THRESHOLD = 0.5;
/** An item applies when the model puts at least this much on "the response attempts it". */
export const APPLIES_THRESHOLD = 0.5;
/** An item passes when at least this much level-probability sits at or above the pass level. */
export const PASS_MASS = 0.5;

export type Provider = 'jev' | 'fallback';

export interface ItemResult {
  id: string;
  name: string;
  applies: boolean;
  appliesProbability: number;
  /** Index into the item's levels: the single most probable level. */
  level: number;
  levelName: string;
  /** In level order. */
  levelProbabilities: number[];
  confidence: number;
  passes: boolean;
  degraded: boolean;
  /** Descending by probability; only sentences at or above EVIDENCE_THRESHOLD. */
  evidence: { sentenceId: string; probability: number }[];
}

export interface ScoreResponse {
  provider: Provider;
  model: string | null;
  sentences: Sentence[];
  items: ItemResult[];
  passedCount: number;
  itemCount: number;
}

export function argmax(values: readonly number[]): number {
  let best = 0;
  for (let i = 1; i < values.length; i += 1) {
    if (values[i]! > values[best]!) best = i;
  }
  return best;
}

/** Does enough level-probability sit at or above `passLevel`? Sums, never counts. */
export function passMass(levelProbabilities: readonly number[], passLevel: number): number {
  let mass = 0;
  for (let i = passLevel; i < levelProbabilities.length; i += 1) mass += levelProbabilities[i]!;
  return mass;
}

export function decidePass(reading: Pick<ItemReading, 'appliesProbability' | 'levelProbabilities'>, passLevel: number): boolean {
  if (reading.appliesProbability < APPLIES_THRESHOLD) return false;
  return passMass(reading.levelProbabilities, passLevel) >= PASS_MASS;
}

export function toItemResult(item: RubricItem, reading: ItemReading, passLevel: number): ItemResult {
  const level = argmax(reading.levelProbabilities);
  const applies = reading.appliesProbability >= APPLIES_THRESHOLD;
  return {
    id: item.id,
    name: item.name,
    applies,
    appliesProbability: reading.appliesProbability,
    level,
    levelName: item.levels[level]?.name ?? String(level),
    levelProbabilities: reading.levelProbabilities,
    confidence: reading.confidence,
    passes: decidePass(reading, passLevel),
    degraded: reading.degraded,
    evidence: reading.sentenceProbabilities
      .filter((s) => s.probability >= EVIDENCE_THRESHOLD)
      .sort((a, b) => b.probability - a.probability),
  };
}

export function summarise(items: readonly ItemResult[]): { passedCount: number; itemCount: number } {
  return {
    passedCount: items.filter((i) => i.passes).length,
    itemCount: items.length,
  };
}

export function buildScoreResponse(
  rubric: Rubric,
  sentences: Sentence[],
  readings: readonly ItemReading[],
  provider: Provider,
  model: string | null,
): ScoreResponse {
  const items = rubric.items.map((item, index) => toItemResult(item, readings[index]!, rubric.passLevel));
  return { provider, model, sentences, items, ...summarise(items) };
}

/** The evidence a teacher reads under a criterion: at or above the cutoff, in the order it was written. */
export function evidenceQuotes(item: ItemResult, sentences: readonly Sentence[], cutoff: number): { id: string; text: string; probability: number }[] {
  if (!item.applies) return [];
  const source = new Map(sentences.map((s, index) => [s.id, { index, text: s.text }]));
  return item.evidence
    .filter((e) => e.probability >= cutoff && source.has(e.sentenceId))
    .sort((a, b) => source.get(a.sentenceId)!.index - source.get(b.sentenceId)!.index)
    .map((e) => ({ id: e.sentenceId, text: source.get(e.sentenceId)!.text, probability: e.probability }));
}

/** Why a criterion is or is not met, in a teacher's words. Uses the same thresholds as decidePass. */
export function passExplanation(facts: { applies: boolean; passes: boolean; mass: number; passName: string }): string {
  const official = 'This is an estimate, not a hard rating or grade.';
  if (!facts.applies) return `Likely not met. The writing doesn't seem to address this criterion. ${official}`;
  const sure = `${Math.round(facts.mass * 100)}%`;
  const needs = `${Math.round(PASS_MASS * 100)}%`;
  return facts.passes
    ? `Likely met. The grader is ${sure} sure this reaches ${facts.passName} or higher, and it needs ${needs}. ${official}`
    : `Likely not met. The grader is only ${sure} sure this reaches ${facts.passName} or higher, and it needs ${needs}. ${official}`;
}
